import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import {
  Plus, Calendar, Users, Loader2, Pencil, Trash2, Gift, ChevronRight
} from 'lucide-react';
import api from '../../services/api';

const WAAnniversaryFlowsPage = () => {
  const navigate = useNavigate();
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  const fetchFlows = async () => {
    try {
      const res = await api.get('/wa/anniversary-flows');
      setFlows(res.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchFlows(); }, []);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const res = await api.post('/wa/anniversary-flows', {
        name: newName, year_templates: [], is_active: true
      });
      navigate(`/whatsapp/anniversary-flows/${res.data.id}`);
    } catch (e) {
      alert(e.response?.data?.detail || 'Error creating flow');
    }
    setCreating(false);
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete anniversary flow "${name}"? This removes all entries.`)) return;
    try {
      await api.delete(`/wa/anniversary-flows/${id}`);
      fetchFlows();
    } catch {}
  };

  if (loading) return <div className="flex justify-center p-12"><Loader2 className="animate-spin text-[#4F46E5]" size={32} /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Gift className="text-[#4F46E5]" size={28} /> Anniversary Flows
          </h1>
          <p className="text-gray-500 text-sm mt-1">Yearly anniversary messages based on first purchase date</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-[#4F46E5] hover:bg-[#4338CA] text-white" data-testid="create-anniversary-btn">
          <Plus size={16} className="mr-2" /> New Anniversary Flow
        </Button>
      </div>

      {flows.length === 0 ? (
        <Card className="p-12 text-center bg-white border border-gray-100">
          <Gift size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-700">No Anniversary Flows</h3>
          <p className="text-gray-400 text-sm mt-1">Create one to send yearly messages to your clients on their purchase anniversary.</p>
        </Card>
      ) : (
        <div className="grid gap-4">
          {flows.map(flow => (
            <Card key={flow.id} className="bg-white border border-gray-100 hover:border-[#4F46E5]/30 transition-colors cursor-pointer" onClick={() => navigate(`/whatsapp/anniversary-flows/${flow.id}`)}>
              <div className="p-5 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#4F46E5]/10 to-purple-100 flex items-center justify-center">
                    <Gift className="text-[#4F46E5]" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{flow.name}</h3>
                    <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                      <span className="flex items-center gap-1"><Calendar size={13} /> {flow.year_templates?.length || 0} year templates</span>
                      <span className="flex items-center gap-1"><Users size={13} /> {flow.stats?.total_clients || 0} clients</span>
                      <Badge variant={flow.is_active ? "default" : "secondary"} className={flow.is_active ? "bg-green-100 text-green-700" : ""}>
                        {flow.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400">
                    {flow.stats?.active || 0} active, {flow.stats?.completed || 0} completed
                  </span>
                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDelete(flow.id, flow.name); }} className="text-red-400 hover:text-red-600">
                    <Trash2 size={14} />
                  </Button>
                  <ChevronRight size={18} className="text-gray-300" />
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader><DialogTitle>New Anniversary Flow</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Input placeholder="Flow name (e.g. Purchase Anniversary)" value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="anniversary-flow-name" />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button onClick={handleCreate} disabled={creating || !newName.trim()} className="bg-[#4F46E5] hover:bg-[#4338CA] text-white" data-testid="create-anniversary-confirm">
                {creating ? <Loader2 className="animate-spin mr-2" size={16} /> : null} Create
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WAAnniversaryFlowsPage;
